"use client";

import { Button } from "@universe/ui/components/button";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@universe/ui/components/input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { AlertCircle, Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import React, { useEffect, useState } from "react";

import { NoTranslate } from "@/components/no-translate";
import { ResponsiveDialog } from "@/components/responsive-dialog";
import useCurrencyFormatter from "@/hooks/useCurrencyFormatter";
import { useOtpAuthMethod } from "@/hooks/useOtpAuthMethod";
import { useSupportEmail } from "@/hooks/useSupportEmail";
import { useOtp } from "@/providers/OtpProvider";

export function OtpModal() {
  const t = useTranslations("otp");
  const tCommon = useTranslations("common");
  const { formatCurrency } = useCurrencyFormatter();
  const { isOtpReady } = useOtpAuthMethod();
  const supportEmail = useSupportEmail();
  const {
    isOpen,
    currentStep,
    config,
    closeModal,
    submitCode,
    resendCode,
    proceedToCodeEntry,
    isLoadingTrustScreen,
  } = useOtp();

  const [otp, setOtp] = useState("");
  // Store config and step locally to prevent flickering when modal closes
  const [localConfig, setLocalConfig] = useState(config);
  const [localStep, setLocalStep] = useState(currentStep);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [hasStartedInitialCooldown, setHasStartedInitialCooldown] =
    useState(false);

  // Update local state when provider state changes and modal is open
  // Use a single effect with proper conditions to avoid cascading renders
  useEffect(() => {
    if (isOpen) {
      // Only update if values have changed
      if (config && config !== localConfig) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- Preserve config during close animation
        setLocalConfig(config);
      }
      if (currentStep !== localStep) {
        setLocalStep(currentStep);
      }
    }
  }, [isOpen, config, currentStep, localConfig, localStep]);

  // Handle modal close reset - separate effect to avoid coupling
  useEffect(() => {
    if (!isOpen) {
      // Clear OTP and reset cooldown when modal closes
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Reset state on modal close
      setOtp("");
      setResendCooldown(0);
      setHasStartedInitialCooldown(false);
    }
  }, [isOpen]);

  // Start resend cooldown when entering code-entry step (only once per modal open)
  useEffect(() => {
    if (localStep === "code-entry" && !hasStartedInitialCooldown) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Start cooldown on step change
      setResendCooldown(10);
      setHasStartedInitialCooldown(true);
    }
  }, [localStep, hasStartedInitialCooldown]);

  // Calculate time remaining from expiryTime
  useEffect(() => {
    if (!localConfig?.expiryTime) return;

    const expiryTime = localConfig.expiryTime;

    const calculateRemaining = () => {
      const expiry = new Date(expiryTime);
      const now = new Date();
      const seconds = Math.floor((expiry.getTime() - now.getTime()) / 1000);
      return seconds > 0 ? seconds : 0;
    };

    // Set initial time
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Initialize timer from expiry time
    setTimeRemaining(calculateRemaining());

    // Update every second
    const interval = setInterval(() => {
      setTimeRemaining(calculateRemaining());
    }, 1000);

    return () => clearInterval(interval);
  }, [localConfig?.expiryTime]);

  // Countdown resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;

    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [resendCooldown]);

  // Format seconds to MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleOtpChange = (value: string) => {
    setOtp(value);
  };

  const handleOtpComplete = async (value: string) => {
    await submitCode(value);
  };

  const handleConfirm = async () => {
    if (otp.length === 4) {
      await submitCode(otp);
    }
  };

  const handleCancel = () => {
    // Call onCancel callback if provided (for all flow types)
    if (localConfig?.onCancel) {
      localConfig.onCancel();
    }
    // Close the OTP modal
    closeModal();
  };

  const handleResendCode = async () => {
    setOtp("");
    await resendCode();
    // Restart cooldown after resending
    setResendCooldown(10);
  };

  const handleGetCode = () => {
    proceedToCodeEntry();
  };

  const isVerifying = localStep === "verifying";

  // Trust screen content
  const trustScreenContent = (
    <div className="space-y-6">
      <div className="text-center space-y-3">
        <div className="text-sm text-gray-800 space-y-3">
          <p>
            {t.rich("trustScreen.description1", {
              recipientName: localConfig?.recipientName || "",
              b: (chunks: React.ReactNode) => (
                <NoTranslate className="font-semibold text-primary">
                  {chunks}
                </NoTranslate>
              ),
            })}
          </p>
          {isOtpReady ? (
            <p>{t("trustScreen.description2")}</p>
          ) : (
            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg flex gap-3 items-start text-left">
              <AlertCircle className="h-5 w-5 text-orange-800 flex-shrink-0 mt-0.5" />
              <p className="text-xs font-semibold text-orange-800">
                {t("trustScreen.otpNotReadyWarning")}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {isOtpReady && (
          <Button
            onClick={handleGetCode}
            disabled={isLoadingTrustScreen}
            className="w-full h-11 text-base"
            data-testid="get-otp-code-button"
          >
            {isLoadingTrustScreen ? (
              <span className="inline-flex items-center justify-center">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <span>
                  {localConfig?.getTrustCodeCta || t("trustScreen.getCodeCta")}
                </span>
              </span>
            ) : (
              <span>
                {localConfig?.getTrustCodeCta || t("trustScreen.getCodeCta")}
              </span>
            )}
          </Button>
        )}
        <Button
          onClick={handleCancel}
          variant="outline"
          className="w-full h-11 text-base"
          data-testid="cancel-trust-button"
        >
          <span>{localConfig?.cancelCta || t("trustScreen.cancelCta")}</span>
        </Button>
      </div>
    </div>
  );

  // Payment code-entry copy. Drop the "to <recipient>" clause when we have no
  // beneficiary name, so it reads "…this payment of <amount>." instead of
  // "…to <blank>." — mirrors the messageFormat.verifyPayment /
  // verifyPaymentGeneric split used for the SMS body.
  const boldPrimary = (chunks: React.ReactNode) => (
    <span className="font-semibold text-primary">{chunks}</span>
  );
  const formattedPaymentAmount =
    formatCurrency(
      localConfig?.paymentAmount || 0,
      localConfig?.paymentCurrency || ""
    ) || "";
  const paymentDescription = localConfig?.recipientName
    ? t.rich("codeEntry.paymentDescription", {
        amount: formattedPaymentAmount,
        recipientName: localConfig.recipientName,
        b: boldPrimary,
      })
    : t.rich("codeEntry.paymentDescriptionGeneric", {
        amount: formattedPaymentAmount,
        b: boldPrimary,
      });

  // Code entry content
  const codeEntryContent = (
    <div className="space-y-6">
      <div className="text-center space-y-3">
        {localConfig?.flowType === "payment" ? (
          <>
            <p className="text-sm text-muted-foreground">
              {paymentDescription}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("codeEntry.paymentCancelInfo")}
            </p>
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              <span>{t("codeEntry.trustDescriptionBefore")}</span>
            </p>
            <p className="text-sm text-muted-foreground">
              <NoTranslate className="font-semibold text-gray-900">
                {localConfig?.recipientName || ""}
              </NoTranslate>{" "}
              <span>{t("codeEntry.trustDescriptionAfter")}</span>
            </p>
          </>
        )}
      </div>

      <div className="flex justify-center">
        <InputOTP
          maxLength={4}
          pattern={REGEXP_ONLY_DIGITS}
          value={otp}
          onChange={handleOtpChange}
          onComplete={handleOtpComplete}
        >
          <InputOTPGroup>
            <InputOTPSlot
              index={0}
              className="!h-12 !w-12 !text-xl lg:!h-14 lg:!w-14 lg:!text-2xl !border-1 !rounded-lg !shadow-sm"
            />
          </InputOTPGroup>
          <InputOTPSeparator />
          <InputOTPGroup>
            <InputOTPSlot
              index={1}
              className="!h-12 !w-12 !text-xl lg:!h-14 lg:!w-14 lg:!text-2xl !border-1 !rounded-lg !shadow-sm"
            />
          </InputOTPGroup>
          <InputOTPSeparator />
          <InputOTPGroup>
            <InputOTPSlot
              index={2}
              className="!h-12 !w-12 !text-xl lg:!h-14 lg:!w-14 lg:!text-2xl !border-1 !rounded-lg !shadow-sm"
            />
          </InputOTPGroup>
          <InputOTPSeparator />
          <InputOTPGroup>
            <InputOTPSlot
              index={3}
              className="!h-12 !w-12 !text-xl lg:!h-14 lg:!w-14 lg:!text-2xl !border-1 !rounded-lg !shadow-sm"
            />
          </InputOTPGroup>
        </InputOTP>
      </div>

      <div className="text-center text-sm space-y-1">
        {localConfig?.expiryTime && (
          <p className="text-xs text-muted-foreground">
            {timeRemaining > 0 ? (
              <span className="inline-flex items-center justify-center gap-1">
                <span>{t("codeEntry.expiresIn")}</span>
                <span className="font-medium inline-block w-[40px] text-center tabular-nums">
                  {formatTime(timeRemaining)}
                </span>
              </span>
            ) : (
              <span className="text-red-600 font-medium">
                {t("codeEntry.codeExpired")}
              </span>
            )}
          </p>
        )}
        <div className="flex flex-wrap justify-center items-center gap-x-1">
          <span className="text-muted-foreground">
            {t("codeEntry.didntReceive")}
          </span>
          <button
            onClick={handleResendCode}
            disabled={resendCooldown > 0}
            className={`font-semibold whitespace-nowrap ${
              resendCooldown > 0
                ? "text-gray-400 !cursor-not-allowed"
                : "text-gray-900 hover:text-gray-700 cursor-pointer"
            }`}
            data-testid="resend-code-button"
          >
            <span>{t("codeEntry.resendCode")}</span>
          </button>
        </div>
      </div>

      <div className="flex gap-3">
        <Button
          onClick={handleCancel}
          variant="outline"
          className="flex-1 h-11 text-base"
          data-testid="cancel-code-entry-button"
        >
          <span>{localConfig?.cancelCta || t("codeEntry.cancelCta")}</span>
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={otp.length !== 4 || isVerifying}
          className="flex-1 h-11 text-base"
          data-testid="confirm-code-button"
        >
          {isVerifying ? (
            <span className="inline-flex items-center justify-center">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              <span>
                {localConfig?.confirmCta || t("codeEntry.confirmCta")}
              </span>
            </span>
          ) : (
            <span>{localConfig?.confirmCta || t("codeEntry.confirmCta")}</span>
          )}
        </Button>
      </div>
    </div>
  );

  // Verifying content
  const verifyingContent = (
    <div className="space-y-6 py-8">
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <Loader2 className="h-12 w-12 text-brand-boundary animate-spin" />
        </div>
        <p className="text-sm text-muted-foreground">
          {t("verifying.description")}
        </p>
      </div>
    </div>
  );

  // Exhausted content
  const exhaustedContent = (
    <div className="space-y-6 py-4">
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <div className="rounded-full bg-red-100 p-3">
            <AlertCircle className="h-10 w-10 text-red-600" />
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-sm font-semibold text-gray-900">
            {t("errors.exhausted")}
          </p>
          <p className="text-sm text-muted-foreground">
            {t("errors.exhaustedDescription", { supportEmail })}
          </p>
        </div>
      </div>
      <div className="space-y-3">
        <Button
          onClick={handleCancel}
          variant="outline"
          className="w-full h-11 text-base"
          data-testid="close-exhausted-button"
        >
          <span>{tCommon("close")}</span>
        </Button>
      </div>
    </div>
  );

  const getContent = () => {
    switch (localStep) {
      case "trust":
        return trustScreenContent;
      case "code-entry":
        return codeEntryContent;
      case "verifying":
        return verifyingContent;
      case "exhausted":
        return exhaustedContent;
      default:
        return null;
    }
  };

  const getTitle = () => {
    switch (localStep) {
      case "trust":
        return localConfig?.title || t("trustScreen.title");
      case "code-entry":
        return t("codeEntry.title");
      case "verifying":
        return t("verifying.title");
      case "exhausted":
        return t("errors.exhausted");
      default:
        return t("codeEntry.title");
    }
  };

  return (
    <ResponsiveDialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isVerifying) {
          handleCancel();
        }
      }}
      title={getTitle()}
      showCloseButton={!isVerifying}
      showDefaultFooter={false}
      preventClose={isVerifying}
      headerClassName="bg-gray-100 pt-4 lg:pt-0 px-6 bg-gradient-to-t from-gray-200 to-white pb-4.5 lg:pb-3 !h-auto !min-h-[46px] !justify-center !items-center overflow-hidden"
    >
      <div className="mx-3 lg:mx-0">{getContent()}</div>
    </ResponsiveDialog>
  );
}
